import { MouseEvent, WheelEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../api/client'
import { clearAnalyticsPageContext, setAnalyticsPageContext, trackBusinessEvent } from '../utils/analytics'
import { getNarrativeConfig } from '../utils/category'
import { requireCredit } from '../utils/billing'
import { useLanguage } from '../hooks/useLanguage'
import { AppBottomNav } from '../components/AppBottomNav'

interface PredictionMarket {
  pm_index: string
  pm_id: string
  pm_slug: string
  pm_title: string
  confidence: number
  match_type: string
  reason: string
}

interface MarketMatchedEvent {
  web3_event_index: number
  event_id: string
  event_title: string
  matched_pm_markets: PredictionMarket[]
}

interface LatestMarketsResponse {
  date: string
  matched_events_count: number
  total_matches: number
  results: MarketMatchedEvent[]
}

const PAGE_SIZE = 20
const SCROLL_KEY = 'markets:scrollY'
const PAGE_KEY = 'markets:page'
const NARRATIVE_KEY = 'markets:selectedNarrative'

const NARRATIVE_LABELS: Record<string, string> = {
  defi: 'DeFi',
  regulation_policy: 'Regulation',
  infrastructure: 'Infrastructure',
  stablecoin: 'Stablecoin',
  rwa: 'RWA',
  ai_crypto: 'AI',
  nft: 'NFT',
  gamefi: 'GameFi',
  socialfi_creator: 'SocialFi',
  governance: 'Governance',
  meme_culture: 'Meme',
  other: 'Other',
}

function formatConfidence(confidence: number) {
  return `${Math.round((confidence || 0) * 100)}%`
}

function formatMatchType(matchType: string) {
  const names: Record<string, string> = {
    same_event: 'Same Event',
    same_specific_event: 'Same Event',
    same_event_chain: 'Event Chain',
    same_company_event_chain: 'Company Event Chain',
    event_impact: 'Event Impact',
    direct_impact: 'Direct Impact',
    same_asset: 'Same Asset',
    same_token: 'Same Asset',
    same_company: 'Same Company',
    same_project: 'Same Project',
    same_entity: 'Same Entity',
    same_specific_entity: 'Same Entity',
    macro_narrative: 'Macro Narrative',
    same_narrative: 'Same Narrative',
  }
  return names[matchType] || matchType.replace(/_/g, ' ')
}

function getBestConfidence(event: MarketMatchedEvent) {
  return Math.max(...event.matched_pm_markets.map((market) => market.confidence || 0))
}

function getMarketCount(event: MarketMatchedEvent) {
  return event.matched_pm_markets.length
}

function getNarrativeId(eventId: string) {
  const normalized = eventId.toLowerCase()
  const match = normalized.match(/^\d{4}\.\d{2}\.\d{2}_(.+)_event_\d+$/)
  return match?.[1] || 'other'
}

function sortEventsByMarketCount(events: MarketMatchedEvent[]) {
  return [...events].sort((a, b) => {
    const marketDiff = getMarketCount(b) - getMarketCount(a)
    if (marketDiff !== 0) return marketDiff
    return getBestConfidence(b) - getBestConfidence(a)
  })
}

function getNarrativeBadge(eventId: string) {
  const narrativeId = getNarrativeId(eventId)
  const config = getNarrativeConfig(narrativeId)
  return {
    label: NARRATIVE_LABELS[narrativeId] || config.shortName || narrativeId,
    config,
  }
}

export function MarketsPage() {
  const navigate = useNavigate()
  const { lang, t } = useLanguage()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const dragStateRef = useRef({ isDown: false, startX: 0, scrollLeft: 0, dragged: false })
  const [data, setData] = useState<LatestMarketsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedNarrative, setSelectedNarrative] = useState<string | undefined>(() => {
    return sessionStorage.getItem(NARRATIVE_KEY) || undefined
  })
  const [page, setPage] = useState(() => {
    const savedPage = Number(sessionStorage.getItem(PAGE_KEY) || '1')
    return Number.isFinite(savedPage) && savedPage > 0 ? savedPage : 1
  })
  const [notice, setNotice] = useState<string | null>(null)
  const restoredScrollRef = useRef(false)
  const didMountRef = useRef(false)

  useEffect(() => {
    let cancelled = false

    async function loadMarkets() {
      setIsLoading(true)
      setError(null)
      try {
        const response = await apiClient.get<LatestMarketsResponse>('/api/polymarket/latest', { lang })
        if (cancelled) return
        setData(response)
        trackBusinessEvent('markets_page_loaded', {
          date: response.date,
          matched_events_count: response.matched_events_count,
          total_matches: response.total_matches,
        })
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load prediction markets')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadMarkets()
    return () => {
      cancelled = true
    }
  }, [lang])

  const narrativeTabs = useMemo(() => {
    const counts = new Map<string, { events: number; markets: number }>()
    for (const event of data?.results || []) {
      const narrativeId = getNarrativeId(event.event_id)
      const existing = counts.get(narrativeId) || { events: 0, markets: 0 }
      counts.set(narrativeId, {
        events: existing.events + 1,
        markets: existing.markets + getMarketCount(event),
      })
    }

    return [...counts.entries()]
      .map(([id, count]) => ({ id, ...count }))
      .sort((a, b) => {
        const marketDiff = b.markets - a.markets
        if (marketDiff !== 0) return marketDiff
        return b.events - a.events
      })
  }, [data])

  const visibleEvents = useMemo(() => {
    const events = data?.results || []
    const filtered = selectedNarrative
      ? events.filter((event) => getNarrativeId(event.event_id) === selectedNarrative)
      : events
    return sortEventsByMarketCount(filtered)
  }, [data, selectedNarrative])

  const displayedEvents = useMemo(() => {
    return visibleEvents.slice(0, page * PAGE_SIZE)
  }, [visibleEvents, page])

  const hasMore = displayedEvents.length < visibleEvents.length

  useEffect(() => {
    const scrollSegment = Math.max(1, Math.ceil(Math.max(displayedEvents.length, 1) / PAGE_SIZE))
    const scrollScope = `markets:${data?.date || 'latest'}:${selectedNarrative || 'all'}:${scrollSegment}`
    setAnalyticsPageContext({
      market_date: data?.date,
      selected_narrative: selectedNarrative || 'all',
      loaded_count: displayedEvents.length,
      total_events: visibleEvents.length,
      total_markets: data?.total_matches || 0,
      current_page: page,
      scroll_segment: scrollSegment,
      scroll_scope: scrollScope,
    })
    return () => clearAnalyticsPageContext([
      'market_date',
      'selected_narrative',
      'loaded_count',
      'total_events',
      'total_markets',
      'current_page',
      'scroll_segment',
      'scroll_scope',
    ])
  }, [data?.date, data?.total_matches, selectedNarrative, displayedEvents.length, visibleEvents.length, page])

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }
    setPage(1)
  }, [selectedNarrative])

  useEffect(() => {
    sessionStorage.setItem(NARRATIVE_KEY, selectedNarrative || '')
  }, [selectedNarrative])

  useEffect(() => {
    sessionStorage.setItem(PAGE_KEY, String(page))
  }, [page])

  useEffect(() => {
    if (restoredScrollRef.current || isLoading || displayedEvents.length === 0) return
    const savedScrollY = Number(sessionStorage.getItem(SCROLL_KEY) || '0')
    if (!savedScrollY) return

    restoredScrollRef.current = true
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: savedScrollY, behavior: 'auto' })
    })
  }, [displayedEvents.length, isLoading])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (container) {
      container.style.scrollbarWidth = 'none'
      ;(container.style as CSSStyleDeclaration & { msOverflowStyle?: string }).msOverflowStyle = 'none'
    }
  }, [])

  const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
    const container = scrollContainerRef.current
    if (!container || container.scrollWidth <= container.clientWidth) return
    event.preventDefault()
    container.scrollLeft += event.deltaY || event.deltaX
  }

  const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    const container = scrollContainerRef.current
    if (!container) return
    dragStateRef.current = {
      isDown: true,
      startX: event.pageX - container.offsetLeft,
      scrollLeft: container.scrollLeft,
      dragged: false,
    }
  }

  const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    const container = scrollContainerRef.current
    const dragState = dragStateRef.current
    if (!container || !dragState.isDown) return
    event.preventDefault()
    const x = event.pageX - container.offsetLeft
    const walk = x - dragState.startX
    if (Math.abs(walk) > 4) dragState.dragged = true
    container.scrollLeft = dragState.scrollLeft - walk
  }

  const stopDrag = () => {
    dragStateRef.current.isDown = false
  }

  const handleTabClick = (callback: () => void) => {
    if (dragStateRef.current.dragged) {
      dragStateRef.current.dragged = false
      return
    }
    callback()
  }

  const handleNarrativeClick = (narrative?: string) => {
    sessionStorage.removeItem(SCROLL_KEY)
    restoredScrollRef.current = true
    window.scrollTo({ top: 0, behavior: 'auto' })
    setPage(1)
    setSelectedNarrative(narrative)
    trackBusinessEvent('markets_narrative_filter', {
      narrative: narrative || 'all',
      date: data?.date,
    })
  }

  const showNotice = (message: string) => {
    setNotice(message)
    window.setTimeout(() => setNotice(null), 1800)
  }

  const handleOpenDetail = async (event: MarketMatchedEvent) => {
    const ok = await requireCredit(
      'view_prediction_markets',
      event.event_id,
      t('查看全部预测市场', 'View All Prediction Markets'),
      showNotice,
      2
    )
    if (!ok) return

    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY))
    sessionStorage.setItem(PAGE_KEY, String(page))
    sessionStorage.setItem(NARRATIVE_KEY, selectedNarrative || '')

    trackBusinessEvent('market_detail_click', {
      event_id: event.event_id,
      market_count: event.matched_pm_markets.length,
      best_confidence: getBestConfidence(event),
    }, event.event_id)
    navigate(`/markets/${encodeURIComponent(event.event_id)}`)
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 dark:bg-gray-950">
      <header className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 z-20">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 p-3">
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                Prediction Market Radar
              </h1>
            </div>
          </div>

          <div className="px-4 pb-3">
            <p className="text-base text-gray-500 dark:text-gray-400 leading-tight">
              {t(
                '汇总近期 Web3 事件与预测市场的关联，帮助你快速发现可下注的市场机会。',
                'Summarizes recent links between Web3 events and prediction markets to help you spot actionable market opportunities.'
              )}
            </p>
            {data && (
              <div className="mt-2 text-sm font-medium text-gray-800 dark:text-gray-100">
                {t(
                  `总计：${data.matched_events_count} events & ${data.total_matches} markets`,
                  `Total: ${data.matched_events_count} events & ${data.total_matches} markets`
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-4 p-4">
        {!isLoading && !error && data && (
          <section className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-2 -mx-4">
            <div
              ref={scrollContainerRef}
              onWheel={handleWheel}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={stopDrag}
              onMouseLeave={stopDrag}
              className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 hide-scrollbar cursor-grab active:cursor-grabbing select-none"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <button
                type="button"
                onClick={() => handleTabClick(() => handleNarrativeClick(undefined))}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                  !selectedNarrative
                    ? 'bg-blue-500 text-white shadow-sm'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <span>🌐</span>
                <span>All</span>
                <span className="opacity-70">{data.total_matches}</span>
              </button>

              {narrativeTabs.map((tab) => {
                const config = getNarrativeConfig(tab.id)
                const label = NARRATIVE_LABELS[tab.id] || config.shortName || tab.id
                const isSelected = selectedNarrative === tab.id
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleTabClick(() => handleNarrativeClick(tab.id))}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                      isSelected
                        ? 'bg-blue-500 text-white shadow-sm'
                        : `${config.bgColor} ${config.color} hover:opacity-80`
                    }`}
                  >
                    <span>{config.emoji}</span>
                    <span>{label}</span>
                    <span className="opacity-70">{tab.markets}</span>
                  </button>
                )
              })}
            </div>
          </section>
        )}

        {isLoading && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
            {t('正在加载预测市场数据...', 'Loading prediction market data...')}
          </div>
        )}

        {!isLoading && error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
            {error}
          </div>
        )}

        {!isLoading && !error && visibleEvents.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
            {t('暂无预测市场匹配。', 'No prediction market matches.')}
          </div>
        )}

        {!isLoading && !error && displayedEvents.length > 0 && (
          <div key={selectedNarrative || 'all'} className="space-y-3">
            {displayedEvents.map((event) => {
              const bestMarket = event.matched_pm_markets[0]
              const bestConfidence = getBestConfidence(event)
              const badge = getNarrativeBadge(event.event_id)
              return (
                <article
                  key={`${selectedNarrative || 'all'}-${event.event_id}`}
                  data-event-id={event.event_id}
                  data-visible-event-id={event.event_id}
                  data-narrative={getNarrativeId(event.event_id)}
                  className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                >
                  <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${badge.config.bgColor} ${badge.config.color}`}>
                      <span>{badge.config.emoji}</span>
                      <span>{badge.label}</span>
                    </span>
                  </div>

                  <h2 className="text-sm font-semibold leading-relaxed text-gray-900 dark:text-white">
                    {event.event_title}
                  </h2>

                  <p className="mt-3 text-sm leading-relaxed text-gray-700 dark:text-gray-200">
                    {t(
                      `发现 ${event.matched_pm_markets.length} 个相关预测市场，最高置信度 ${formatConfidence(bestConfidence)}。`,
                      `Found ${event.matched_pm_markets.length} related prediction markets. Highest confidence: ${formatConfidence(bestConfidence)}.`
                    )}
                  </p>

                  {bestMarket && (
                    <div className="mt-3">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">{t('最佳匹配', 'Best Match')}</div>
                      <div className="mt-1 text-sm leading-snug text-gray-700 dark:text-gray-200">
                        {bestMarket.pm_title}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>{formatConfidence(bestMarket.confidence)}</span>
                        <span>{formatMatchType(bestMarket.match_type)}</span>
                        <span>{bestMarket.pm_index}</span>
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => handleOpenDetail(event)}
                    className="mt-3 w-full rounded-md bg-blue-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-600"
                  >
                    {t('查看全部预测市场', 'View All Prediction Markets')}
                  </button>
                </article>
              )
            })}

            {hasMore && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => {
                    trackBusinessEvent('markets_load_more', {
                      narrative: selectedNarrative || 'all',
                      current_page: page,
                      loaded_count: displayedEvents.length,
                      total: visibleEvents.length,
                    })
                    setPage((p) => p + 1)
                  }}
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50 transition-colors"
                >
                  {t('加载更多', 'Load More')}
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {notice && (
        <div className="fixed left-4 right-4 top-4 z-40 mx-auto max-w-sm rounded-lg bg-gray-900 px-4 py-3 text-center text-sm font-medium text-white shadow-lg shadow-gray-900/20 dark:bg-white dark:text-gray-950">
          {notice}
        </div>
      )}
      <AppBottomNav />
    </div>
  )
}
